/**
 * Numbers and dates, said the way somebody says them out loud.
 *
 * Two naming conventions live here on purpose. `days`/`shortDate`/`monthName`
 * are this platform's own and read as sentences. `formatDate`/`daysUntil`/
 * `formatCurrency` are the names the design's components already call, and
 * renaming them would mean editing every component to no effect.
 */

/** "3 days", "1 day", "today" — never a signed number a reader has to translate. */
export function days(n: number): string {
  if (n === 0) return 'today';
  return n === 1 ? '1 day' : `${n} days`;
}

export function shortDate(iso: string): string {
  // `T00:00` and no timezone, so the browser reads it as local midnight and
  // the date shown is the date stored.
  return new Date(`${iso}T00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short'
  });
}

export function monthName(iso: string): string {
  return new Date(`${iso}T00:00`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric'
  });
}

/** Initials for an avatar: first and last, or the first two letters of one name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** `YYYY-MM-DD` to "8 Sep". Returns the input unchanged if it is not a date. */
export function formatDate(
  iso: string,
  opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
): string {
  const d = new Date(`${iso}T00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, opts);
}

/**
 * How a full date reads, from the signed-in person's preferences. Set by the
 * auth context on sign-in; short dates ("8 Sep") are left alone because a
 * day and a month read the same in every one of these formats.
 */
let DATE_FORMAT: string | undefined;

export function setDateFormat(format: string | undefined): void {
  DATE_FORMAT = format;
}

export function formatDateLong(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m !== null && DATE_FORMAT !== undefined && DATE_FORMAT !== 'D MMM YYYY') {
    const [, y, mo, d] = m;
    if (DATE_FORMAT === 'DD/MM/YYYY') return `${d}/${mo}/${y}`;
    if (DATE_FORMAT === 'MM/DD/YYYY') return `${mo}/${d}/${y}`;
    if (DATE_FORMAT === 'YYYY-MM-DD') return `${y}-${mo}-${d}`;
  }
  return formatDate(iso, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * The same date, short enough to sit in a form field.
 *
 * "8 September 2026" is what a date reads as in running text, and it is too
 * long for a control in a two-column grid — it truncates to "September 30,
 * 2..." which is worse than a shorter month. The chosen formats are honoured
 * exactly as above; only the studio's own default is abbreviated, to the
 * "D MMM YYYY" it is already called in Settings.
 */
export function formatDateShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m !== null && DATE_FORMAT !== undefined && DATE_FORMAT !== 'D MMM YYYY') {
    return formatDateLong(iso);
  }
  return formatDate(iso, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Whole rupees.
 *
 * No decimals: retainers are whole thousands, and "₹45,000.00" is noise on a
 * 390px screen. The currency is a parameter rather than baked in because the
 * server sends one per client and one studio does eventually take a client
 * who pays in something else.
 */
export function formatCurrency(amount: number, currency = 'INR'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  } catch {
    // An unknown ISO code throws rather than falling back, and a screen that
    // will not render because somebody typed one is worse than one showing
    // the code itself.
    return `${currency} ${Math.round(amount).toLocaleString()}`;
  }
}

/**
 * Days from today to a date. Negative is in the past.
 *
 * Both sides are pinned to midnight before subtracting, so an answer never
 * comes back as 0.97 days and rounds to the wrong day for somebody looking at
 * their phone at 11pm.
 */
export function daysUntil(iso: string): number {
  const target = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(target)) return 0;
  return Math.round((target - Date.parse(`${today()}T00:00:00Z`)) / 86_400_000);
}

/** `YYYY-MM-DD` for a `Date`, in local time rather than UTC. */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The studio's timezone, once the workspace has said what it is.
 *
 * Module-level because `today()` and `daysUntil()` are plain functions called
 * from thirty places, several of them outside a component, and threading a
 * hook through all of them to deliver one deployment-wide fact would be worse
 * than this. `WorkspaceProvider` sets it once.
 *
 * Until then it is the browser's own zone, which is right for everybody in the
 * office and is what this did before — so nothing renders wrong in the moment
 * between the first paint and the workspace arriving.
 */
let studioZone: string | null = null;

export function setStudioZone(zone: string): void {
  studioZone = zone;
}

/**
 * Today, where the studio is — the same day the server means.
 *
 * It used to be the browser's day, which is right until somebody opens the app
 * from another country and sees a different set of work overdue than the
 * person sitting next to the server does.
 */
export function today(): string {
  const now = new Date();
  if (studioZone === null) return isoDate(now);

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: studioZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);

  const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  const [year, month, day] = [part('year'), part('month'), part('day')];
  // A zone the browser does not know would leave these blank rather than
  // throw, and a blank date is worse than the browser's own.
  if (year === '' || month === '' || day === '') return isoDate(now);
  return `${year}-${month}-${day}`;
}

export function formatDateTime(isoStr: string) {
  const d = new Date(isoStr)
  if (Number.isNaN(d.getTime())) return isoStr
  // Rendered in the workspace's configured time zone (Settings > Workspace)
  // so a timestamp reads the same for everyone on the team, wherever they
  // happen to be.
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: studioZone || undefined,
  })
}

/** The first of the month a date falls in — the shape the cycles table stores. */
export function monthOf(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** "2 hours ago", "yesterday" — for an activity feed, where exact times are noise. */
export function relativeTime(isoTimestamp: string): string {
  const then = new Date(isoTimestamp).getTime();
  if (Number.isNaN(then)) return isoTimestamp;
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const d = Math.round(hours / 24);
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  return formatDate(isoTimestamp.slice(0, 10));
}

const AVATAR_PALETTE = ['#3b7dde', '#2f9e57', '#d98e1c', '#FC2265', '#8a5cf5', '#0ea5a3', '#e0433a', '#fd5c86']

/** Deterministic avatar color for an id/name that isn't tied to a client or
 * team-member record (e.g. the signed-in account posting a task note) — same
 * input always maps to the same color, without needing to store one. */
export function avatarColorFor(seed: string) {
  let hash = 0
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]
}
